import { useEffect, useState } from "react";
import { BsCart2, BsEye, BsX } from "react-icons/bs";
import { FaAngleRight } from "react-icons/fa6";
import { MdOutlineEventNote, MdOutlineKeyboardBackspace } from "react-icons/md";
import { RiCustomerService2Line } from "react-icons/ri";
import { TfiBolt } from "react-icons/tfi";
import { BiX } from "react-icons/bi";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import {
  getRestaurantOverview,
  getPendingDocuments,
  getActivityLogs,
  reviewDocument,
  resetReviewDocumentStatus,
  ReviewDocumentRequest,
  PendingDocument,
  PendingDocumentItem,
} from "../../redux/restaurantOverview/restaurantOverviewSlice";
import LoadingSpinner from "../../components/elements/LoadingSpinner";

const Index = () => {
  const dispatch = useAppDispatch();
  const {
    overview,
    overviewStatus,
    overviewError,
    pendingDocuments,
    pendingDocumentsStatus,
    activityLogs,
    activityLogsStatus,
    reviewDocumentStatus,
    reviewDocumentError,
  } = useAppSelector((state: RootState) => state.restaurantOverview);

  const [reviewingDocumentId, setReviewingDocumentId] = useState<string | null>(
    null
  );
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<PendingDocument | null>(null);
  const [reviewingDocumentType, setReviewingDocumentType] = useState<
    string | null
  >(null);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(getRestaurantOverview());
    // Note: getPendingDocuments and getActivityLogs are not needed since
    // the overview endpoint returns all the data we need
  }, [dispatch]);

  // Debug log to check data structure
  useEffect(() => {
    console.log("Overview data:", overview);
    console.log("Pending documents from Redux state:", pendingDocuments);
    console.log("Activity logs from Redux state:", activityLogs);

    if (pendingDocuments.length > 0) {
      console.log("Total pending documents:", pendingDocuments.length);
      console.log(
        "Documents with pending status:",
        pendingDocuments.filter((item) => item.status === "pending").length
      );
      console.log("First document:", pendingDocuments[0]);
    }
    if (activityLogs.length > 0) {
      console.log("Activity logs data:", activityLogs);
      console.log("First activity:", activityLogs[0]);
    }
  }, [overview, pendingDocuments, activityLogs]);

  // Handle review document success/failure - refresh data
  useEffect(() => {
    if (reviewDocumentStatus === "success") {
      dispatch(resetReviewDocumentStatus());
      // Refresh the overview data (which includes pending documents and activity logs)
      dispatch(getRestaurantOverview());
    }
  }, [reviewDocumentStatus, dispatch]);

  const handleReviewDocument = async (
    documentId: string,
    restaurantId: string,
    documentType: string,
    action: "approve" | "reject"
  ) => {
    setReviewingDocumentId(documentId);
    setReviewingDocumentType(documentType);

    // Show loading toast
    const toastId = toast.loading(
      `${action === "approve" ? "Approving" : "Rejecting"} ${documentType}...`
    );

    const reviewData: ReviewDocumentRequest = {
      action,
      restaurantId,
      documentType,
    };

    try {
      await dispatch(reviewDocument({ documentId, reviewData })).unwrap();
      toast.success(
        `${documentType} ${
          action === "approve" ? "approved" : "rejected"
        } successfully!`,
        { id: toastId }
      );
    } catch (error) {
      toast.error(`Failed to ${action} ${documentType}`, { id: toastId });
    } finally {
      setReviewingDocumentId(null);
      setReviewingDocumentType(null);
    }
  };

  const handleDocumentReview = (document: PendingDocumentItem) => {
    // Convert PendingDocumentItem to PendingDocument format for modal compatibility
    const formattedDocument: PendingDocument = {
      documentId: document.documentId,
      restaurantId: document.restaurantId,
      restaurantName: document.restaurantName,
      legalDocuments: document.legalDocuments,
      bankDetails: document.bankDetails,
      status: document.status,
      submissionDate: document.submissionDate,
      reviewDate: document.reviewDate || null,
      reviewNotes: document.reviewNotes || null,
      createdAt: document.createdAt || document.submissionDate,
      updatedAt: document.updatedAt || document.submissionDate,
    };
    setSelectedDocument(formattedDocument);
    setShowDocumentModal(true);
  };

  const handleBankDetailsView = (document: PendingDocumentItem) => {
    // Convert PendingDocumentItem to PendingDocument format for modal compatibility
    const formattedDocument: PendingDocument = {
      documentId: document.documentId,
      restaurantId: document.restaurantId,
      restaurantName: document.restaurantName,
      legalDocuments: document.legalDocuments,
      bankDetails: document.bankDetails,
      status: document.status,
      submissionDate: document.submissionDate,
      reviewDate: document.reviewDate || null,
      reviewNotes: document.reviewNotes || null,
      createdAt: document.createdAt || document.submissionDate,
      updatedAt: document.updatedAt || document.submissionDate,
    };
    setSelectedDocument(formattedDocument);
    setShowBankDetailsModal(true);
  };

  const closeModals = () => {
    setShowDocumentModal(false);
    setShowBankDetailsModal(false);
    setSelectedDocument(null);
    setReviewingDocumentId(null);
    setReviewingDocumentType(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getTimeAgo = (dateString: string | undefined | null) => {
    if (!dateString) return "Unknown time";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";

      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInMins = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMins / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInMins < 1) {
        return "Just now";
      } else if (diffInMins < 60) {
        return `${diffInMins} mins ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours} hours ago`;
      } else {
        return `${diffInDays} days ago`;
      }
    } catch (error) {
      return "Invalid date";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "complete":
      case "approved":
        return "bg-green-100 text-green-600";
      case "pending":
        return "bg-yellow-100 text-yellow-600";
      case "rejected":
        return "bg-red-100 text-red-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const formatDocumentType = (documentType: string | undefined | null) => {
    if (!documentType) return "Unknown Document Type";
    return documentType
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Dashboard metrics from API data
  const dashboardMetrics = [
    {
      title: "Total Restaurants",
      value: overview?.counts.totalRestaurants?.toString() || "0",
      change: "Real-time data",
      icon: <MdOutlineEventNote className="w-5 h-5" />,
    },
    {
      title: "Active Orders",
      value: overview?.counts.activeOrders?.toString() || "0",
      change: "Real-time data",
      icon: <BsCart2 className="w-5 h-5" />,
    },
    {
      title: "Pending Payments",
      value: overview?.counts.pendingPayments
        ? `₦${overview.counts.pendingPayments.toLocaleString()}`
        : "₦0",
      change: "Real-time data",
      icon: <TfiBolt className="w-5 h-5" />,
    },
    // {
    //   title: "Support Ticket",
    //   value: "123", // This might need another endpoint
    //   change: "Static for now",
    //   icon: <RiCustomerService2Line className="w-5 h-5" />,
    // },
  ];

  return (
    <section className="font-mont">
      <div className="flex items-start gap-4">
        <Link to="/dashboard/restaurants">
          <button className="cursor-pointer p-2 rounded-full bg-[#008B5014]">
            <MdOutlineKeyboardBackspace className="w-6 h-6" />
          </button>
        </Link>
        <article>
          <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
            <span className="font-semibold">Restaurant</span> <FaAngleRight />
            Overview
          </h1>
          <p className="text-sm">
            <Link to="management">
              Overview of FoodBank Restaurant platform operations and key
              metrics.
            </Link>
          </p>
        </article>
      </div>

      <section className="overflow-x-auto my-5">
        {overviewStatus === "loading" ? (
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner />
          </div>
        ) : overviewError ? (
          <div className="text-center py-8 text-red-500">
            Error loading overview: {overviewError}
          </div>
        ) : (
          <article className="flex justify-between items-center gap-3 pb-5">
            {dashboardMetrics.map((item, idx) => {
              // Apply yellow border and shadow for 2nd and 4th items (idx 1 and 3)
              const isOrange = idx === 1 || idx === 3;
              const borderClass = isOrange
                ? "border-orange100"
                : "border-secondary";
              const shadowClass = isOrange
                ? "shadow-orange100"
                : "shadow-secondary";
              return (
                <div
                  key={item.title}
                  className={`border p-4 min-w-[280px] rounded-lg shadow-md mt-4 ${borderClass} ${shadowClass}`}
                >
                  <div className="flex items-center gap-2 justify-between">
                    <h3 className="font-medium text-lg">{item.title}</h3>
                    {item.icon}
                  </div>
                  <p className="text-2xl font-semibold mb-2 mt-4">
                    {item.value}
                  </p>
                  <p className="text-sm text-gray-500">{item.change}</p>
                </div>
              );
            })}
          </article>
        )}
      </section>
      <section className="flex gap-5 items-start">
        <section className="bg-[#EEEEEE40] p-4 rounded-md w-2/3">
          <h3 className="text-xl font-semibold mb-2">Recent Activities</h3>
          <p className="text-sm">Latest platform events and updates</p>
          <section className="overflow-y-auto">
            <ul className="h-[22rem]">
              {activityLogsStatus === "loading" ||
              overviewStatus === "loading" ? (
                <li className="flex justify-center items-center py-8">
                  <LoadingSpinner />
                </li>
              ) : activityLogs &&
                Array.isArray(activityLogs) &&
                activityLogs.length > 0 ? (
                activityLogs
                  .filter((activity) => activity && activity._id)
                  .map((activity) => (
                    <li key={activity._id} className="my-4">
                      <article className="flex items-center justify-between gap-4 py-3">
                        <div>
                          <h4 className="font-medium text-lg">
                            {activity.description || "No description available"}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {activity.restaurantName || "Unknown Restaurant"} •{" "}
                            {getTimeAgo(activity.createdAt)} •{" "}
                            {activity.activityType || "Unknown Activity"}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="p-2 w-28 bg-green-100 text-green-600 rounded-md"
                        >
                          Success
                        </button>
                      </article>
                    </li>
                  ))
              ) : (
                <li className="text-center py-8 text-gray-500">
                  No recent activities found
                </li>
              )}
            </ul>
          </section>
        </section>
        <section className="bg-[#EEEEEE40] p-4 rounded-md w-1/3">
          <h3 className="text-xl font-semibold mb-2">Pending Approval</h3>
          <p className="text-sm">Items Requiring your attention</p>
          <section className="overflow-y-auto">
            <div className="h-[39rem] mt-3 space-y-4">
              {pendingDocumentsStatus === "loading" ||
              overviewStatus === "loading" ? (
                <div className="flex justify-center items-center py-8">
                  <LoadingSpinner />
                </div>
              ) : pendingDocuments &&
                Array.isArray(pendingDocuments) &&
                pendingDocuments.length > 0 ? (
                pendingDocuments
                  .filter(
                    (item) =>
                      item && item.documentId && item.status === "pending"
                  )
                  .map((item) => (
                    <div key={item.documentId} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2">
                          <p className="font-medium">
                            {item.restaurantName || "Unknown Restaurant"}
                          </p>
                          <p className="text-sm text-gray-500">
                            Restaurant Registration
                          </p>
                          <p className="text-xs text-gray-500">
                            {getTimeAgo(item.submissionDate)}
                          </p>
                        </div>
                        <div className="text-xs text-gray-500">
                          {/* Count available documents */}
                          {
                            [
                              item.legalDocuments?.cacRegistrationCertificate,
                              item.legalDocuments?.foodLisence,
                              item.legalDocuments?.taxIdentificationNumber,
                              item.bankDetails?.bankName,
                            ].filter(Boolean).length
                          }{" "}
                          documents
                        </div>
                      </div>
                      <button
                        className="w-full bg-green-600 text-white hover:bg-green-700 p-2 rounded-md transition-colors"
                        onClick={() => handleDocumentReview(item)}
                      >
                        Review
                      </button>
                    </div>
                  ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No pending documents found
                </div>
              )}
            </div>
          </section>
        </section>
      </section>

      {/* Document Review Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-green-600">
                  Restaurant Approval: {selectedDocument.restaurantName}
                </h3>
                <p className="text-sm text-gray-500">
                  Restaurant Details and Documentation
                </p>
              </div>
              <button
                className="p-2 hover:bg-gray-100 rounded-full"
                onClick={closeModals}
              >
                <BiX className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Restaurant Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Restaurant ID:</p>
                  <p className="text-sm text-gray-500">
                    {selectedDocument.restaurantId}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Status:</p>
                  <p
                    className={`text-sm inline-block px-2 py-1 rounded ${getStatusColor(
                      selectedDocument.status
                    )}`}
                  >
                    {selectedDocument.status}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Submission Date:</p>
                  <p className="text-sm text-gray-500">
                    {getTimeAgo(selectedDocument.submissionDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Review Date:</p>
                  <p className="text-sm text-gray-500">
                    {selectedDocument.reviewDate
                      ? getTimeAgo(selectedDocument.reviewDate)
                      : "Not reviewed yet"}
                  </p>
                </div>
              </div>

              {/* Legal Documents */}
              <div>
                <p className="text-sm font-medium mb-3">Legal Documents:</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <span className="text-sm">
                      CAC Registration Certificate
                    </span>
                    <div className="flex items-center gap-2">
                      {selectedDocument.legalDocuments
                        ?.cacRegistrationCertificate &&
                      selectedDocument.legalDocuments
                        .cacRegistrationCertificate !== "https://" ? (
                        <>
                          <span className="text-xs text-green-600">
                            ✓ Uploaded
                          </span>
                          <button
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                            onClick={() =>
                              window.open(
                                selectedDocument.legalDocuments
                                  .cacRegistrationCertificate,
                                "_blank"
                              )
                            }
                          >
                            <BsEye className="w-3 h-3" />
                            View
                          </button>
                          <div className="flex gap-1">
                            <button
                              className="px-2 py-1 text-xs bg-green-600 text-white hover:bg-green-700 rounded disabled:opacity-50"
                              disabled={
                                reviewingDocumentType ===
                                "cacRegistrationCertificate"
                              }
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "cacRegistrationCertificate",
                                  "approve"
                                )
                              }
                            >
                              {reviewingDocumentType ===
                              "cacRegistrationCertificate"
                                ? "Approving..."
                                : "Approve"}
                            </button>
                            <button
                              className="px-2 py-1 text-xs bg-red-600 text-white hover:bg-red-700 rounded disabled:opacity-50"
                              disabled={
                                reviewingDocumentType ===
                                "cacRegistrationCertificate"
                              }
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "cacRegistrationCertificate",
                                  "reject"
                                )
                              }
                            >
                              {reviewingDocumentType ===
                              "cacRegistrationCertificate"
                                ? "Rejecting..."
                                : "Reject"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-red-600">
                          ✗ Not uploaded
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded">
                    <span className="text-sm">Food License</span>
                    <div className="flex items-center gap-2">
                      {selectedDocument.legalDocuments?.foodLisence &&
                      selectedDocument.legalDocuments.foodLisence !==
                        "https://" ? (
                        <>
                          <span className="text-xs text-green-600">
                            ✓ Uploaded
                          </span>
                          <button
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                            onClick={() =>
                              window.open(
                                selectedDocument.legalDocuments.foodLisence,
                                "_blank"
                              )
                            }
                          >
                            <BsEye className="w-3 h-3" />
                            View
                          </button>
                          <div className="flex gap-1">
                            <button
                              className="px-2 py-1 text-xs bg-green-600 text-white hover:bg-green-700 rounded disabled:opacity-50"
                              disabled={reviewingDocumentType === "foodLisence"}
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "foodLisence",
                                  "approve"
                                )
                              }
                            >
                              {reviewingDocumentType === "foodLisence"
                                ? "Approving..."
                                : "Approve"}
                            </button>
                            <button
                              className="px-2 py-1 text-xs bg-red-600 text-white hover:bg-red-700 rounded disabled:opacity-50"
                              disabled={reviewingDocumentType === "foodLisence"}
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "foodLisence",
                                  "reject"
                                )
                              }
                            >
                              {reviewingDocumentType === "foodLisence"
                                ? "Rejecting..."
                                : "Reject"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-red-600">
                          ✗ Not uploaded
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded">
                    <span className="text-sm">Tax Identification Number</span>
                    <div className="flex items-center gap-2">
                      {selectedDocument.legalDocuments
                        ?.taxIdentificationNumber ? (
                        <>
                          <span className="text-xs text-green-600">
                            ✓ Provided
                          </span>
                          <span className="text-xs text-gray-600">
                            {
                              selectedDocument.legalDocuments
                                .taxIdentificationNumber
                            }
                          </span>
                          <div className="flex gap-1">
                            <button
                              className="px-2 py-1 text-xs bg-green-600 text-white hover:bg-green-700 rounded disabled:opacity-50"
                              disabled={
                                reviewingDocumentType ===
                                "taxIdentificationNumber"
                              }
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "taxIdentificationNumber",
                                  "approve"
                                )
                              }
                            >
                              {reviewingDocumentType ===
                              "taxIdentificationNumber"
                                ? "Approving..."
                                : "Approve"}
                            </button>
                            <button
                              className="px-2 py-1 text-xs bg-red-600 text-white hover:bg-red-700 rounded disabled:opacity-50"
                              disabled={
                                reviewingDocumentType ===
                                "taxIdentificationNumber"
                              }
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "taxIdentificationNumber",
                                  "reject"
                                )
                              }
                            >
                              {reviewingDocumentType ===
                              "taxIdentificationNumber"
                                ? "Rejecting..."
                                : "Reject"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-red-600">
                          ✗ Not provided
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded">
                    <span className="text-sm">Bank Details</span>
                    <div className="flex items-center gap-2">
                      {selectedDocument.bankDetails?.bankName ? (
                        <>
                          <span className="text-xs text-green-600">
                            ✓ Provided
                          </span>
                          <button
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                            onClick={() =>
                              handleBankDetailsView(selectedDocument)
                            }
                          >
                            <BsEye className="w-3 h-3" />
                            View
                          </button>
                          <div className="flex gap-1">
                            <button
                              className="px-2 py-1 text-xs bg-green-600 text-white hover:bg-green-700 rounded disabled:opacity-50"
                              disabled={reviewingDocumentType === "bankDetails"}
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "bankDetails",
                                  "approve"
                                )
                              }
                            >
                              {reviewingDocumentType === "bankDetails"
                                ? "Approving..."
                                : "Approve"}
                            </button>
                            <button
                              className="px-2 py-1 text-xs bg-red-600 text-white hover:bg-red-700 rounded disabled:opacity-50"
                              disabled={reviewingDocumentType === "bankDetails"}
                              onClick={() =>
                                handleReviewDocument(
                                  selectedDocument.documentId,
                                  selectedDocument.restaurantId,
                                  "bankDetails",
                                  "reject"
                                )
                              }
                            >
                              {reviewingDocumentType === "bankDetails"
                                ? "Rejecting..."
                                : "Reject"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-red-600">
                          ✗ Not provided
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Review Notes */}
              {selectedDocument.reviewNotes && (
                <div>
                  <p className="text-sm font-medium mb-2">Review Notes:</p>
                  <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                    {selectedDocument.reviewNotes}
                  </p>
                </div>
              )}

              {/* Instructions */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 text-center">
                  Review each document individually using the approve/reject
                  buttons above.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bank Details Modal */}
      {showBankDetailsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-green-600">
                  Bank Account Details
                </h3>
                <p className="text-sm text-gray-500">
                  Restaurant Bank Information
                </p>
              </div>
              <button
                className="p-2 hover:bg-gray-100 rounded-full"
                onClick={closeModals}
              >
                <BsX className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Account Number:</p>
                  <p className="text-sm text-gray-500">
                    {selectedDocument.bankDetails?.accountNumber ||
                      "Not provided"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Account Name:</p>
                  <p className="text-sm text-gray-500">
                    {selectedDocument.bankDetails?.accountName ||
                      "Not provided"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Bank Name:</p>
                  <p className="text-sm text-gray-500">
                    {selectedDocument.bankDetails?.bankName || "Not provided"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Bank Code:</p>
                  <p className="text-sm text-gray-500">
                    {selectedDocument.bankDetails?.bankCode || "Not provided"}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium">Status:</p>
                <p className="text-sm text-gray-500">
                  {selectedDocument.bankDetails?.inUse ? "Active" : "Inactive"}
                </p>
              </div>

              <div className="pt-4">
                <button
                  className="w-full bg-green-600 text-white hover:bg-green-700 py-2 px-4 rounded-md transition-colors"
                  onClick={() => setShowBankDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Index;
